/**
 * Metadata Field Components
 *
 * Reusable components for displaying label-value pairs in metadata panels.
 * Used consistently across all detail page right panels (samples, aggregates, locations, etc.)
 */

import { cn } from "@/lib/utils/general-utils";
import { ExternalLink as ArrowTopRightOnSquareIcon } from "lucide-react";
import Link from "next/link";

/**
 * MetadataLabel - Consistent label styling for metadata fields
 */
export interface MetadataLabelProps {
  /** Label text */
  readonly children: React.ReactNode;
  /** Optional additional className */
  readonly className?: string;
}

export function MetadataLabel({ children, className }: MetadataLabelProps) {
  return (
    <div
      className={cn(
        "text-[10px] font-medium text-text-tertiary uppercase tracking-wider mb-0.5",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * MetadataValue - Consistent value styling for metadata fields
 */
export interface MetadataValueProps {
  /** Value content */
  readonly children: React.ReactNode;
  /** Whether the value is empty/null (shows gray text) */
  readonly isEmpty?: boolean;
  /** Use monospace font (for codes, sizes, etc.) */
  readonly mono?: boolean;
  /** Optional additional className */
  readonly className?: string;
}

export function MetadataValue({
  children,
  isEmpty = false,
  mono = false,
  className,
}: MetadataValueProps) {
  return (
    <div
      className={cn(
        "text-sm font-medium",
        isEmpty ? "text-text-tertiary" : "text-gray-900",
        mono && "font-mono",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * MetadataLink - Link with external icon for navigation to related entities
 */
export interface MetadataLinkProps {
  /** Link destination */
  readonly href: string;
  /** Link text */
  readonly children: React.ReactNode;
  /** Optional additional className */
  readonly className?: string;
}

export function MetadataLink({ href, children, className }: MetadataLinkProps) {
  return (
    <Link
      href={href}
      className={cn(
        "text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-150 inline-flex items-center gap-1",
        className,
      )}
    >
      <span>{children}</span>
      <ArrowTopRightOnSquareIcon className="w-3 h-3 flex-shrink-0" />
    </Link>
  );
}

/**
 * MetadataField - Combined label + value field
 * Convenience component for the most common pattern
 */
export interface MetadataFieldProps {
  /** Field label */
  readonly label: string;
  /** Field value (renders empty state if nullish) */
  readonly value: React.ReactNode;
  /** Custom empty placeholder (defaults to "—") */
  readonly emptyPlaceholder?: string;
  /** Use monospace font for value */
  readonly mono?: boolean;
  /** Link href (if value should be a link) */
  readonly href?: string;
  /** Optional additional className for container */
  readonly className?: string;
}

export function MetadataField({
  label,
  value,
  emptyPlaceholder = "—",
  mono = false,
  href,
  className,
}: MetadataFieldProps) {
  const isEmpty = value == null || value === "";
  const displayValue = isEmpty ? emptyPlaceholder : value;

  return (
    <div className={className}>
      <MetadataLabel>{label}</MetadataLabel>
      {href && !isEmpty ? (
        <MetadataLink href={href}>{displayValue}</MetadataLink>
      ) : (
        <MetadataValue isEmpty={isEmpty} mono={mono}>
          {displayValue}
        </MetadataValue>
      )}
    </div>
  );
}
